import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import MainApi from "../../../../../../services/mainApi.service";
import { IHealthChecks } from "../../../../../../models/health-checks";
import { IScheduleFilterUser } from "../../../../../../models/schedule-filter-user";

const HEALTH_CHECK_ROUTE =
  process.env.REACT_APP_CALENDAR + "/api/v1/ScheduleFilterUser/";

const healthCheckService = new MainApi<IScheduleFilterUser>(HEALTH_CHECK_ROUTE);

const translationPath = "page.settings.schedule.scheduleFilterUser.";

function joinExternalNames(
  items?: Array<{ externalName?: string | null }> | null
): string {
  if (!items?.length) return "-";
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of items) {
    const name = (it?.externalName ?? "").trim();
    if (!name) continue;
    const key = name.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out.length ? out.join("; ") : "-";
}

const ScheduleFilterUserPage = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { eI } = useParams();

  const columns: GridColDef<IScheduleFilterUser>[] = useMemo(
    () => [
      {
        field: "user",
        headerName: t(translationPath + "user") || "User",
        renderCell: (params) => {
          return params.value ? params.value.fullName : "-";
        },
      },
      {
        field: "scheduleFilterUserCalendars",
        headerName:
          t(translationPath + "scheduleFilterUserCalendars") || "Calendars",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const text = joinExternalNames(params.value);
          return <OverflowTooltip text={text} tooltip={text} />;
        },
      },
    ],
    [t]
  );

  const handleRowClick = (params: GridRowParams<IScheduleFilterUser>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const handleAdd = () => {
    navigate("new");
  };

  return (
    <Table<IScheduleFilterUser>
      showDefaultMacroFilters={false}
      service={healthCheckService}
      columns={columns}
      title={
        t(translationPath + "schedule_filter_user") || "Schedule Filter User"
      }
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      searchInput={false}
      backButton
      backButtonUrl={`/e/${eI}/dashboard/settings`}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for a schedule filter user"
      }
      addButtonLabel={
        t(translationPath + "add_schedule_filter_user") ||
        "Add Schedule Filter User"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default ScheduleFilterUserPage;
